import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    output,
    signal,
    TemplateRef,
    viewChild,
    ViewEncapsulation,
} from "@angular/core";
import { Doc } from "./s25.document.const";
import { ReportBuilderCell } from "./s25.document.report.builder.table.component";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { DocumentService } from "../../../services/document.service";
import { Bind } from "../../../decorators/bind.decorator";
import { S25Util } from "../../../util/s25-util";
import { ReportBuilderUtil } from "./s25.report.builder.util";
import { ModalService } from "../../modal/modal.service";

@Component({
    standalone: false,
    selector: "s25-ng-document-report-builder-cell-editor",
    template: `
        <div>
            <label>Label</label>
            <input
                class="c-input label-input"
                [class.invalid]="!!cell().errors().label"
                type="text"
                [(ngModel)]="cell().label"
                (ngModelChange)="updateDisplay(cell())"
            />
            @if (cell().errors().label) {
                <span class="error ngRed">{{ cell().errors().label }}</span>
            }
        </div>
        <div>
            <label>Width</label>
            <input
                #widthInput
                class="c-input width-input"
                type="number"
                min="0"
                max="100"
                [ngModel]="cell().width() | number: '1.1-1'"
                (ngModelChange)="onCellWidthChange(widthInput, $event)"
                (keydown.enter)="widthChange.emit(widthInput.valueAsNumber)"
                (blur)="widthChange.emit(widthInput.valueAsNumber)"
            />
        </div>
        <div>
            <label>Type</label>
            <select [(ngModel)]="cell().variable().type" (ngModelChange)="onVariableTypeChange()">
                <option [value]="'variable'">Variable</option>
                <option [value]="'function'">Function</option>
                <option [value]="'subreport'">Subreport</option>
            </select>
        </div>
        <div>
            <label>Data</label>
            <s25-ng-bpe-variable
                [state]="cell().variable().state"
                [type]="templateVariableKey()"
                [codeMode]="false"
                [documentType]="documentType()"
                [hasPost]="false"
                [hasFormatExample]="false"
                [prefix]="false"
                [wrap]="false"
                [hasNoneItem]="true"
                [itemFilter]="addSubreports"
                [placeholder]="templateVariablePlaceholder()"
                [scrollIntoView]="false"
                (variableChange)="onVariableChange(cell())"
            />
            @if (cell().variable().type() === "subreport" && cell().variable().subreportId) {
                <div class="buttons">
                    @if (cell().variable().type() === "subreport" && cell().variable().subreportId >= 0) {
                        <s25-ng-button [type]="'outline'" [onClick]="onEditSubreport" [onClickArgs]="['edit']">
                            Edit
                        </s25-ng-button>
                    }
                    <s25-ng-button [type]="'outline'" [onClick]="onEditSubreport" [onClickArgs]="['copy']">
                        Copy
                    </s25-ng-button>
                </div>
            }
            @if (cell().errors().variable) {
                <span class="error ngRed">{{ cell().errors().variable }}</span>
            }
            @if (cell().variable().type() === "function" && !!cell().variable().state.var) {
                <s25-ng-document-report-builder-cell-editor-function
                    [(arguments)]="cell().variable().arguments"
                    [type]="$any(cell().variable().state.var)"
                    [documentType]="documentType()"
                    (changed)="updateDisplay(cell())"
                />
            }
        </div>
        <div>
            <label>Styles</label>
            <s25-ng-css-rule
                [(styles)]="cell().css"
                [allowedProperties]="Doc.supportedCssProperties"
                [validateProperty]="ReportBuilderUtil.validateCssProperty"
                [(valid)]="cssValid"
                (validChange)="validate()"
                (stylesChange)="changed.emit()"
            />
        </div>

        <ng-template #editDocument let-id="id" let-mode="mode" let-scope="scope" let-modal="modal">
            <s25-ng-edit-document
                [id]="id"
                [mode]="mode"
                [scope]="scope"
                (saved)="modal.close({ id: $event })"
                (cancelled)="modal.close()"
            />
        </ng-template>
    `,
    styles: `
        :host {
            --border-width: 1px;
            --border-color: #0002;
            --border-radius: 3px;
            --resize-handle-width: 5px;
            --control-bg: #f2f2f2;
            --green: #60a725;
            --red: #d62000;
            --red-alpha: color-mix(in lch, var(--red), #fff0 85%);
            --cell-highlight: color-mix(in lch, var(--cn-blue), #fff0 85%);
        }

        ::ng-deep body.nm-party--on s25-ng-document-report-builder-cell-editor {
        }

        :host {
            display: grid;
            gap: 0.5rem;
            grid-template-columns: 100%;
        }

        label {
            font-weight: bold;
        }

        :host > div {
            display: grid;
            gap: 0.1rem;
            grid-template-columns: 100%;
        }

        select {
            align-items: center;
            background-color: var(--cn-blue);
            border-radius: 3px;
            color: white !important;
            border: 0;
            padding-block: 0.2em;
        }

        ::ng-deep ngb-popover-window:has(s25-ng-document-report-builder-cell-editor) {
            --bs-popover-max-width: 350px !important;
            width: 350px;
        }

        ::ng-deep s25-ng-document-report-builder-cell-editor s25-ng-bpe-variable .selectedVarLabel {
            display: none;
        }

        ::ng-deep s25-ng-document-report-builder-cell-editor s25-dropdown-paginated .select2-choice {
            padding-left: 0 !important;
        }

        .invalid.invalid {
            border-color: var(--conflict-color) !important;
            background: color-mix(in hsl, var(--conflict-color), transparent 90%);
        }

        .error {
            font-size: 0.9em;
            display: block;
        }

        .buttons {
            display: flex;
            column-gap: 0.5rem;
            padding-top: 0.25rem;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentReportBuilderCellEditorComponent {
    documentType = input.required<Doc.ReportBuilderTypeId>();
    cell = input.required<ReportBuilderCell>();

    changed = output<void>();
    widthChange = output<number>();
    hide = output<Promise<{ refresh: boolean }>>();
    editDocumentTemplate = viewChild("editDocument", { read: TemplateRef });
    cssValid = signal(true);

    protected readonly Doc = Doc;
    protected readonly ReportBuilderUtil = ReportBuilderUtil;
    modalService = inject(ModalService);

    templateVariableKey = computed(() => {
        const type = this.cell().variable().type();
        if (type === "variable") return this.documentType();
        if (type === "function") return "reportBuilderFunctions";
        if (type === "subreport") return "none";
    });
    templateVariablePlaceholder = computed(() => S25Util.firstCharToUpper(this.cell().variable().type()));

    @Bind
    async addSubreports(items: DropDownItem[]) {
        if (this.cell().variable().type() !== "subreport") return items;
        if (Doc.type[this.documentType()].type === "subreport") return items; // No nested subreports

        const subreports = await DocumentService.getDocumentList(null, "subreport");
        const mapped = subreports.map((subreport) => ({
            val: `subreport.${subreport.itemId}`,
            txt: subreport.itemName,
            grp: "Subreports",
        }));
        const newItems: DropDownItem[] = [{ isGroup: true, groupName: "Subreports" }, ...mapped];

        // If a template is already selected, include it in the list
        if (this.cell().variable().subreportId < 0) {
            newItems.unshift(this.cell().variable().state.variable);
        }

        return newItems;
    }

    onVariableTypeChange() {
        this.cell().variable().state.variable = undefined;
        this.cell().variable().arguments.set([]);
    }

    onVariableChange(cell: ReportBuilderCell) {
        if (cell.variable().type() === "subreport" && cell.variable().state.variable) {
            cell.variable().subreportId = Number(cell.variable().state.variable.val.split(".")[1]);
        }
        this.cell().variable().arguments.set([]);
        this.updateDisplay(cell);
    }

    updateDisplay(cell: ReportBuilderCell) {
        const value = ReportBuilderUtil.getCellDisplayValue(cell);
        cell.display.set(value);
        this.onChange();
    }

    onChange() {
        this.validate();
        this.changed.emit();
    }

    // We update widths on enter and blur, but if a user clicks the input's native arrow controls the element never
    // gets focused, so it won't update on blur. So, we need to update widths if there is a change to the value when
    // the element is not focused.
    onCellWidthChange(input: HTMLElement, width: number) {
        if (document.activeElement === input) return;
        this.widthChange.emit(width);
    }

    validate() {
        // We don't need to validate css because we already have a "cssValid" signal from the CSS component
        ReportBuilderUtil.validateCell(this.cell(), { validateCss: false });
        if (!this.cssValid()) {
            this.cell().errorMessage.set("Invalid styles");
            this.cell().valid.set(false);
        }
    }

    @Bind
    async onEditSubreport(mode: Doc.Mode) {
        if (!this.editDocumentTemplate()) return;

        const document = await DocumentService.getDocument(this.cell().variable().subreportId);

        const reopen = S25Util.createPromise<{ refresh: boolean }>();
        this.hide.emit(reopen.promise);
        const modal = this.modalService.s25Modal<{ id: number }>({
            title: `${S25Util.toTitleCase(mode)} Subreport: ${document.document_name}`,
            size: "xl",
            resizable: true,
            bodyTemplate: this.editDocumentTemplate(),
            templateData: { id: document.document_id, scope: "subreport", mode },
        });
        const newDocument = await modal.closed;
        if (newDocument) {
            this.cell().variable().state.variable = { val: `subreport.${newDocument.id}` };
            this.onVariableChange(this.cell());
        }
        // Only need to refresh on edit. On copy there's a new ID, so it refreshes automatically
        reopen.resolve({ refresh: mode === "edit" && !!newDocument });
    }
}
